/* ============================================
   SkillLibrary — Read-only worker skill cards
   Shows all skills for a given agent from
   agente_worker_skills table.
   ============================================ */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'

interface WorkerSkill {
  id: number
  agente_id: number
  worker_id: string
  nombre: string
  instrucciones: string | null
  modelo: string | null
  contexto_requerido: string[] | null
  output_format: string | null
  activo: boolean | null
  created_at: string | null
  temperatura?: number | null
  max_tokens?: number | null
}

interface Props {
  agentId: number
  agentName: string
}

const SKILL_CATEGORIES: Record<string, { label: string; icon: string; workerIds: string[] }> = {
  content: {
    label: 'Content Creation',
    icon: '📱',
    workerIds: ['instagram_writer', 'facebook_writer', 'twitter_writer', 'pinterest_writer', 'linkedin_writer', 'tiktok_writer'],
  },
  strategy: {
    label: 'Strategy & Brand',
    icon: '🎯',
    workerIds: ['brand_analyst', 'content_strategist', 'hashtag_researcher', 'visual_advisor'],
  },
  production: {
    label: 'Production',
    icon: '🛠',
    workerIds: ['caption_writer', 'calendar_planner', 'multichannel_adapter', 'content_reviewer', 'image_prompt_writer', 'analytics_advisor'],
  },
}

const MODEL_LABELS: Record<string, string> = {
  'gemini-2.5-pro': 'Gemini 2.5 Pro',
  'gemini-2.5-flash': 'Gemini 2.5 Flash',
  'gemini-2.0-flash': 'Gemini 2.0 Flash',
}

export default function SkillLibrary({ agentId, agentName }: Props) {
  const [skills, setSkills] = useState<WorkerSkill[]>([])
  const [loading, setLoading] = useState(true)
  const [expandedId, setExpandedId] = useState<number | null>(null)

  const loadSkills = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await supabase
        .from('agente_worker_skills')
        .select('*')
        .eq('agente_id', agentId)
        .order('worker_id', { ascending: true })

      if (data) setSkills(data as unknown as WorkerSkill[])
    } catch (err) {
      console.error('Error loading skills:', err)
    }
    setLoading(false)
  }, [agentId])

  useEffect(() => { loadSkills() }, [loadSkills])

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>Cargando skills...</div>
  }

  if (skills.length === 0) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
        <p style={{ fontSize: 15, marginBottom: 8 }}>No hay skills configurados</p>
        <p style={{ fontSize: 13 }}>Los skills se configuran via CRON o manualmente en la tabla agente_worker_skills.</p>
      </div>
    )
  }

  const activeSkills = skills.filter(s => s.activo !== false)

  return (
    <div className="card" style={{ marginBottom: 24 }}>
      <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
            </svg>
            Skills de {agentName}
          </h2>
          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            {activeSkills.length} skills activos &middot; Configuracion read-only
          </span>
        </div>
      </div>

      {Object.entries(SKILL_CATEGORIES).map(([catKey, cat]) => {
        const catSkills = activeSkills.filter(s => cat.workerIds.includes(s.worker_id))
        if (catSkills.length === 0) return null

        return (
          <div key={catKey} style={{ padding: '0 20px 20px' }}>
            <h3 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              {cat.icon} {cat.label} <span style={{ fontWeight: 400, fontSize: 11, opacity: 0.6 }}>({catSkills.length})</span>
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
              {catSkills.map(skill => {
                const isExpanded = expandedId === skill.id
                const modelLabel = MODEL_LABELS[skill.modelo || ''] || skill.modelo || 'Default'

                return (
                  <button
                    key={skill.id}
                    type="button"
                    onClick={() => setExpandedId(isExpanded ? null : skill.id)}
                    style={{
                      textAlign: 'left', border: `1px solid ${isExpanded ? 'var(--color-primary)' : 'var(--border-color)'}`,
                      borderRadius: 12, padding: 14, background: isExpanded ? 'var(--bg-card-hover)' : 'var(--bg-surface)',
                      cursor: 'pointer', transition: 'all 0.15s', width: '100%',
                    }}
                  >
                    {/* Header row */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: 8, background: 'var(--bg-tertiary)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 14, flexShrink: 0,
                      }}>
                        {cat.icon}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{skill.nombre}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 1 }}>
                          <code style={{ fontSize: 11, background: 'var(--bg-tertiary)', padding: '1px 6px', borderRadius: 4 }}>{skill.worker_id}</code>
                          {' '}&middot;{' '}{modelLabel}{' '}&middot;{' '}{skill.output_format === 'json' ? 'json' : 'texto'}
                        </div>
                      </div>
                      <span style={{ fontSize: 18, color: 'var(--text-secondary)', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'none' }}>
                        ▾
                      </span>
                    </div>

                    {/* Expanded content */}
                    {isExpanded && skill.instrucciones && (
                      <div style={{
                        marginTop: 12, padding: 14, background: 'var(--bg-tertiary)', borderRadius: 8,
                        border: '1px solid var(--border-color)',
                        fontSize: 13, lineHeight: 1.7, color: 'var(--text-primary)',
                        whiteSpace: 'pre-wrap', fontFamily: '"JetBrains Mono", monospace', maxHeight: 300, overflow: 'auto',
                      }}>
                        {skill.instrucciones}
                      </div>
                    )}

                    {/* Context badges */}
                    {isExpanded && skill.contexto_requerido && (skill.contexto_requerido as string[]).length > 0 && (
                      <div style={{ display: 'flex', gap: 4, marginTop: 10, flexWrap: 'wrap' }}>
                        {(skill.contexto_requerido as string[]).map(ctx => (
                          <span key={ctx} style={{
                            fontSize: 10, fontWeight: 500, padding: '2px 8px', borderRadius: 12,
                            background: 'color-mix(in srgb, var(--primary-light) 8%, transparent)', color: 'var(--color-primary)',
                            border: '1px solid color-mix(in srgb, var(--brand-500) 15%, transparent)',
                          }}>
                            {ctx}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Parameters */}
                    {isExpanded && (
                      <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 11, color: 'var(--text-secondary)' }}>
                        {skill.temperatura !== undefined && skill.temperatura !== null && (
                          <span>🌡 Temp: {skill.temperatura}</span>
                        )}
                        {skill.max_tokens && (
                          <span>📊 Tokens: {skill.max_tokens}</span>
                        )}
                      </div>
                    )}
                  </button>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}
